import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Alert, Box, Button, Card, CardContent, CardMedia, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Select, Snackbar, TextField, Typography } from '@mui/material';
import { CheckBox, People, Sort, WorkOutline } from '@mui/icons-material';
import Header from '../../components/Header';
import HeaderImage from '../../images/header_bg_5.jpg'
import axiosInstance from '../../utils/axiosInstance';

const SingleChallenge = () => {
    const { ID } = useParams();

    const [challenge, setChallenge] = useState({});
    const [participants, setParticipants] = useState([]);
    const [open, setOpen] = useState(false);
    const [openNoti, setOpenNoti] = useState(false);
    const [message, setMessage] = useState('');
    const [filteredParticipants, setFilteredParticipants] = useState([]);
    const [sortOrder, setSortOrder] = useState('asc');
    const [searchQuery, setSearchQuery] = useState('');
    const auth = localStorage.getItem('gym_user') ? JSON.parse(localStorage.getItem('gym_user')) : null;

    const handleOpenModal = () => setOpen(true);
    const handleCloseModal = () => setOpen(false);
    const getParticipantsRef = useRef()

    const handleJoin = async () => {
        setOpenNoti(true);
        const payload = {
            user_id: auth.member_id || auth.guest_id || 0,
            challenge_id: ID,
            points_earned: 0,
            penalty_applied: false,
            completed: false
        };

        if (!auth.member_id && !auth.guest_id) {
            setMessage('Only logged in guests or members can join')
            setTimeout(() => { setOpenNoti(false) }, 2000)
            return;
        }

        await axiosInstance.post(`paticipations/create`, payload, {
                headers: { 'Content-Type': 'application/json' }
            }).then(() => {
            setMessage(`Success, successfully added participant to ${challenge.title}`)
            if (getParticipantsRef.current) getParticipantsRef.current();
            setTimeout(() => {
                handleCloseModal()
                setOpenNoti(false)
            }, 2000)
        }).catch((error) => {
            if (error.status === 404) {
                setMessage(`Failure, Participants not found`)
            } else if (error.status === 400) {
                setMessage(`Failure, ${error.response.error}`)
            } else if (error.status === 409) {
                setMessage(`Alert! User already joined this challenge`)
            } else {
                setMessage(`Failure, ${error}`)
            }
            setTimeout(() => { setOpenNoti(false) }, 2000);
        })
    }

    const sortParticipants = (participants, order) => {
        return [...participants].sort((a, b) => {
            return order === 'asc' ? a.points_earned - b.points_earned : b.points_earned - a.points_earned;
        });
    };

    const filterParticipants = (participants, query) => {
        return participants.filter(
            (p) =>
                p.name.toLowerCase().includes(query.toLowerCase()) ||
                p.points_earned.toString().includes(query)
        );
    };

    useEffect(() => {
        const fetchChallengeDetails = async () => {
            try {
                const response = await axiosInstance.get(`challenges/${ID}`);
                setChallenge(response.data.data);
            } catch (error) {
                setChallenge({})
            }
        };

        const getParticipants = async () => {
            await axiosInstance.get(`paticipations/challenge/${ID}`).then((response) => {
                const sorted = sortParticipants(response.data.data, sortOrder);
                setParticipants(sorted);
                
                setFilteredParticipants(filterParticipants(sorted, searchQuery));
            }).catch(() => {
                setParticipants([]);
                setFilteredParticipants([]);
            });
        };

        getParticipantsRef.current = getParticipants;

        fetchChallengeDetails();
        getParticipants()
    }, [ ID, searchQuery, sortOrder]);

    const handleSortChange = (event) => {
        setSortOrder(event.target.value);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setFilteredParticipants(filterParticipants(participants, event.target.value));
    };

    if (!challenge) {
        return (
            <div className="w-full text-center mt-10 text-red-500 font-bold text-xl">
                Loading challenge details...
            </div>
        );
    }

    return (
        <>
            <Header title="Challenging you" image={HeaderImage}>
                Lorem ipsum dolor sit amet consecteturrepellendus
                vero esse perspiciatis deserunt soluta, omnis nam error rem eaque quia recusandae aut?
            </Header>
            <div className='container flex items-center mt-10 text-3xl text-white gap-4'>
                <span className='bg-[#24245c] p-3 rounded-3xl px-5'><WorkOutline className='text-[#c7b262] text-[3rem]' /></span>
                <span className='text-[2.4rem]'>Challnge Participations</span>
            </div>
            <div className="container mx-auto p-4 flex flex-wrap w-full justify-between pt-16">
                {/* Challenge Details */}
                <div className='w-[25%]'>
                    <Card className="shadow-lg h-auto">
                        <CardMedia sx={{ height: 300 }} image={challenge.img_url || 'https://via.placeholder.com/600x300'} title={challenge.title} />
                        <CardContent>
                            <Typography variant="h4" className="text-green-600 font-bold mb-2">
                                {challenge.title}
                            </Typography>
                            <Typography variant="body1" className="mb-4">
                                {challenge.description}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" className="mb-4">
                                <strong>Category:</strong> {challenge.category || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" className="mb-4">
                                <strong>Points:</strong> {challenge.points || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" className="mb-4">
                                <strong>Duration:</strong> {challenge.duration || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" className="mb-4">
                                <strong>Difficulty:</strong> {challenge.difficulty || 'N/A'}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Take Part Button */}
                    {(!auth.guest_id || !auth.member_id) && !participants.some((participant) => participant.user_id === auth.guest_id || participant.user_id === auth.member_id) && (
                        <Box className="mt-6 text-center">
                            <Button variant="contained" color="primary" onClick={handleOpenModal}>
                                Take Part in challenge
                            </Button>
                        </Box>
                    )}
                </div>

                {/* Participants Section */}
                <Box className="w-[72%]">
                    <div className="flex justify-between items-center mb-8 w-full">
                        <Typography variant="h5" className="font-bold mb-4 flex items-center">
                            <People className="mr-2" /> Participants ({filteredParticipants.length})
                        </Typography>

                        <div className="flex items-center bg-white p-2 gap-3">
                            <FormControl size="small" className="mr-4">
                                <InputLabel>Sort By <Sort /></InputLabel>
                                <Select label={`Sort By ${() => <Sort />}`} value={sortOrder} onChange={handleSortChange}>
                                    <MenuItem value="asc">Low to High</MenuItem>
                                    <MenuItem value="desc">High to Low</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField size="small" placeholder="Search by name or points" value={searchQuery} onChange={handleSearchChange} />
                        </div>
                    </div>
                    {filteredParticipants.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                            {filteredParticipants.map((participant) => (
                                <Card key={participant.participation_id} className="shadow-md">
                                    <CardMedia component="img" alt="Participant" height={120} image={participant.image_url || 'https://via.placeholder.com/100x100'} />
                                    <CardContent>
                                        <Typography variant="inherit" className="font-bold">
                                            Participant: {participant.name}
                                        </Typography>
                                        {/* <Typography variant="body2" color="textSecondary">
                                            Challenge ID: {participant.challenge_id}
                                        </Typography> */}
                                        <Typography variant="body2" color="textSecondary">
                                            Points Earned: {participant.points_earned}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Penalty Applied: {participant.penalty_applied ? 'Yes' : 'No'}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Completed: {participant.completed ? 'Yes' : 'No'}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Started: {new Date(participant.createdAt).toLocaleDateString()}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Typography variant="body1" color="error">
                            No participants found.
                        </Typography>
                    )}
                </Box>

                <Dialog open={open} onClose={handleCloseModal}>
                    <DialogTitle className="text-blue-950">Confirmation</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Ready to take part in {challenge.title} challenge?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModal} color="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleJoin} color="success" variant="outlined">
                            <CheckBox /> Continue
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={openNoti} autoHideDuration={20000} onClose={() => setOpenNoti(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <Alert color={message.startsWith("Success") ? 'success' : message.startsWith('Failure') ? 'error' : message.startsWith('Warning') ? 'warning' : 'info'}>{message}</Alert>
                </Snackbar>
            </div>
        </>
    );
};

export default SingleChallenge;
